import React, { useState } from "react";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Select, Table, Tag, Typography, Upload } from "antd";
import dayjs from "dayjs";

import {
  useRemoveMusicTrack,
  useStudioMusic,
  useUploadMusicTrack,
  type MusicTrack,
  type MusicVibe,
} from "../hooks/useStudioMusic";

const VIBES: { value: MusicVibe; label: string; hint: string }[] = [
  { value: "calm", label: "Spokojny", hint: "porady, wyjaśnienia, edukacja" },
  { value: "energetic", label: "Energetyczny", hint: "motywacja, transformacje, CTA" },
  { value: "story", label: "Historia", hint: "anegdoty, przemiany klientek" },
];

/**
 * Biblioteka podkładów (etap C2): utwory z licencją, po trzech „vibe'ach". Render dobiera
 * podkład do klipu, wycisza go pod głosem (sidechain) i zaczyna od beat-dropu, gdy utwór ma
 * długi wstęp. Bez utworów w bibliotece render idzie bez muzyki — nic się nie psuje.
 */
export const MusicPanel = (): React.ReactElement => {
  const tracksQuery = useStudioMusic();
  const upload = useUploadMusicTrack();
  const remove = useRemoveMusicTrack();
  const [vibe, setVibe] = useState<MusicVibe>("calm");

  return (
    <div className="flex flex-col gap-4">
      <Typography.Paragraph type="secondary" className="!mb-0 text-sm">
        Tylko utwory z licencją na użycie w reklamach. Plik MP3 albo WAV, najlepiej 60–120 s, bez wokalu. Wybierz vibe,
        potem wgraj plik — tytuł bierze się z nazwy pliku.
      </Typography.Paragraph>
      <div className="flex flex-wrap items-center gap-3">
        <Select
          className="w-64"
          value={vibe}
          onChange={setVibe}
          options={VIBES.map((entry) => ({ value: entry.value, label: `${entry.label} · ${entry.hint}` }))}
        />
        <Upload
          accept="audio/mpeg,audio/wav,audio/x-wav,.mp3,.wav"
          showUploadList={false}
          customRequest={({ file, onSuccess, onError }) => {
            const audio = file as File;
            upload.mutate(
              { file: audio, vibe, title: audio.name.replace(/\.[^.]+$/, "") },
              { onSuccess: () => onSuccess?.({}), onError: (error) => onError?.(error as Error) },
            );
          }}
        >
          <Button icon={<UploadOutlined />} loading={upload.isLoading}>
            Wgraj utwór
          </Button>
        </Upload>
      </div>

      <Table
        dataSource={tracksQuery.data ?? []}
        loading={tracksQuery.isLoading}
        rowKey="key"
        size="small"
        pagination={false}
        locale={{ emptyText: "Brak podkładów — klipy renderują się bez muzyki." }}
      >
        <Table.Column<MusicTrack> title="Utwór" dataIndex="title" />
        <Table.Column<MusicTrack>
          title="Vibe"
          dataIndex="vibe"
          width={140}
          render={(value: MusicVibe) => <Tag>{VIBES.find((entry) => entry.value === value)?.label ?? value}</Tag>}
        />
        <Table.Column<MusicTrack>
          title="Dodano"
          dataIndex="addedAt"
          width={120}
          render={(addedAt: string) => dayjs(addedAt).format("DD.MM.YYYY")}
        />
        <Table.Column<MusicTrack>
          title=""
          width={60}
          render={(_, track) => (
            <Popconfirm
              title="Usunąć utwór z biblioteki?"
              okText="Usuń"
              cancelText="Anuluj"
              onConfirm={() => remove.mutate(track.key)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        />
      </Table>
    </div>
  );
};
